// ShogiAI.js
// Şogi yapay zekası: negamax alfa-beta (PVS), iteratif derinleştirme, transpozisyon
// tablosu, quiescence (taş alma uzantısı), şah uzantısı, katil/geçmiş sezgileri.
// Arama, motorun iç make/unmake API'sini (_gen/_make/_unmake) kullanır; motorun genel
// durumunu (geçmiş kaydı vb.) değiştirmez — arama bitince pozisyon aynen geri gelir.
//
// Seviyeler:
//   easy   : 1 ply, quiescence yok, yüksek gürültü (bariz taş kaybı yapabilir)
//   medium : 2 ply alfa-beta + kısa quiescence, düşük gürültü
//   hard   : iteratif derinleştirme, ~700 ms
//   expert : iteratif derinleştirme, ~1800 ms, şah güvenliği daha ayrıntılı değerlendirme
//
// API:
//   chooseMove(engine, { level }) -> tanımlayıcı | null       (senkron)
//   playAiMove(engine, options)   -> hamle kaydı | null       (senkron, geriye uyum)
//   computeAiMove(engine, { level }) -> Promise<tanımlayıcı | null>  (Web Worker)
//   AI_LEVELS

import { Shogi, STEP_HIT } from './ShogiEngine';

export const AI_LEVELS = ['easy', 'medium', 'hard', 'expert'];

const LEVEL_CONFIG = {
  easy: { maxDepth: 1, timeMs: 400, noise: 320, qMax: 0, expertEval: false },
  medium: { maxDepth: 2, timeMs: 900, noise: 50, qMax: 4, expertEval: false },
  hard: { maxDepth: 12, timeMs: 700, noise: 12, qMax: 8, expertEval: false },
  expert: { maxDepth: 16, timeMs: 1800, noise: 0, qMax: 10, expertEval: true },
};

// Taş değerleri (iç kod = tip; 9..15 terfili)
//            -   P    L    N    S    G    B    R   K   +P   +L   +N   +S   -   +B    +R
const VAL = [0, 90, 260, 300, 420, 500, 650, 750, 0, 520, 500, 510, 500, 0, 900, 1050];
// Eldeki taş (tahtadakinden biraz değerli)
const HAND_VAL = [0, 105, 290, 330, 460, 540, 720, 830];
// Şah yakınındayken saldırgan katkısı
const ATK_W = [0, 4, 8, 10, 16, 18, 14, 18, 0, 18, 16, 16, 16, 0, 26, 32];

const INF = 1e9;
const MATE = 1e6;
const MATE_BOUND = MATE - 2000;

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

// ------------------------------------------------------------------ Değerlendirme

// Sıradaki oyuncunun bakış açısından puan.
function evaluate(e, expert) {
  const B = e._sq;
  const ks = e._king[0];
  const kg = e._king[1];
  const ksr = ks >= 0 ? (ks / 9) | 0 : -9;
  const ksc = ks >= 0 ? ks % 9 : -9;
  const kgr = kg >= 0 ? (kg / 9) | 0 : -9;
  const kgc = kg >= 0 ? kg % 9 : -9;
  let score = 0;
  let atkOnGote = 0; // sentenin gote şahına baskısı
  let atkOnSente = 0;
  let defS = 0;
  let defG = 0;
  for (let i = 0; i < 81; i++) {
    const p = B[i];
    if (p === 0) continue;
    const t = p > 0 ? p : -p;
    const r = (i / 9) | 0;
    const c = i % 9;
    let v = VAL[t];
    if (p > 0) {
      if (t === 1) v += r < 6 ? (6 - r) * 4 : 0;
      else if (t === 6 || t === 7 || t === 14 || t === 15) v += mobility(B, r, c, t, 1);
      const dE = Math.max(Math.abs(r - kgr), Math.abs(c - kgc));
      if (dE <= 2) atkOnGote += ATK_W[t] * (dE === 1 ? 2 : 1);
      if (t !== 8) {
        const dO = Math.max(Math.abs(r - ksr), Math.abs(c - ksc));
        if (dO <= 1 && (t === 4 || t === 5 || (t >= 9 && t <= 12))) defS += 2;
        else if (dO <= 2 && (t === 4 || t === 5 || t === 3 || t === 2 || t === 1)) defS += 1;
      }
      score += v;
    } else {
      if (t === 1) v += r > 2 ? (r - 2) * 4 : 0;
      else if (t === 6 || t === 7 || t === 14 || t === 15) v += mobility(B, r, c, t, -1);
      const dE = Math.max(Math.abs(r - ksr), Math.abs(c - ksc));
      if (dE <= 2) atkOnSente += ATK_W[t] * (dE === 1 ? 2 : 1);
      if (t !== 8) {
        const dO = Math.max(Math.abs(r - kgr), Math.abs(c - kgc));
        if (dO <= 1 && (t === 4 || t === 5 || (t >= 9 && t <= 12))) defG += 2;
        else if (dO <= 2 && (t === 4 || t === 5 || t === 3 || t === 2 || t === 1)) defG += 1;
      }
      score -= v;
    }
  }
  const hs = e._hand[0];
  const hg = e._hand[1];
  let handS = 0;
  let handG = 0;
  for (let t = 1; t <= 7; t++) {
    score += HAND_VAL[t] * hs[t] - HAND_VAL[t] * hg[t];
    handS += hs[t] * (t >= 5 ? 2 : 1);
    handG += hg[t] * (t >= 5 ? 2 : 1);
  }
  // şah güvenliği: savunmacılar artı, saldırganlar ve rakibin eli eksi
  score += defS * 12 - defG * 12;
  score += atkOnGote - atkOnSente;
  // açık şah + rakibin elinde taş => tehlike
  const exposedG = kg >= 0 ? exposure(B, kgr, kgc) : 0;
  const exposedS = ks >= 0 ? exposure(B, ksr, ksc) : 0;
  score += exposedG * handS * 3 - exposedS * handG * 3;
  // şahı geride tutmak
  if (ks >= 0) score += ksr >= 7 ? 15 : -10 * (7 - ksr);
  if (kg >= 0) score -= kgr <= 1 ? 15 : -10 * (kgr - 1);
  if (expert) {
    if (ks >= 0) score -= kingZoneAttacks(e, ksr, ksc, -1) * 28;
    if (kg >= 0) score += kingZoneAttacks(e, kgr, kgc, 1) * 28;
  }
  return (score + (e._stm > 0 ? 20 : -20)) * e._stm;
}

function mobility(B, r, c, t, side) {
  let n = 0;
  const dirs = t === 6 || t === 14 ? DIAG4 : ORTH4;
  for (let d = 0; d < 4; d++) {
    let rr = r + dirs[d][0];
    let cc = c + dirs[d][1];
    while (rr >= 0 && rr <= 8 && cc >= 0 && cc <= 8) {
      n++;
      if (B[rr * 9 + cc] !== 0) break;
      rr += dirs[d][0];
      cc += dirs[d][1];
    }
  }
  return n * 3 - 20;
}
const DIAG4 = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
const ORTH4 = [[1, 0], [-1, 0], [0, 1], [0, -1]];

// Şahın çevresindeki boş kare sayısı (rakibin atma yapabileceği yerler)
function exposure(B, r, c) {
  let n = 0;
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (!dr && !dc) continue;
      const rr = r + dr;
      const cc = c + dc;
      if (rr < 0 || rr > 8 || cc < 0 || cc > 8) continue;
      if (B[rr * 9 + cc] === 0) n++;
    }
  }
  return n;
}

function kingZoneAttacks(e, r, c, by) {
  let n = 0;
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (!dr && !dc) continue;
      const rr = r + dr;
      const cc = c + dc;
      if (rr < 0 || rr > 8 || cc < 0 || cc > 8) continue;
      if (e._attacked(rr, cc, by)) n++;
    }
  }
  return n;
}

// ------------------------------------------------------------------ Yardımcılar

// Hamle sonrası taş doğrudan rakip şaha saldırıyor mu? (yaklaşık: açılan şahları saymaz)
function givesDirectCheck(e, m, side) {
  const ek = e._king[side > 0 ? 1 : 0];
  if (ek < 0) return false;
  const from = m & 127;
  const to = (m >> 7) & 127;
  let t;
  if (from >= 81) t = from - 81;
  else {
    const p = e._sq[from];
    t = (p > 0 ? p : -p) + ((m >> 14) & 1 ? 8 : 0);
  }
  const tr = (to / 9) | 0;
  const tc = to % 9;
  const er = (ek / 9) | 0;
  const ec = ek % 9;
  const dr = er - tr;
  const dc = ec - tc;
  const f = side > 0 ? -1 : 1;
  if (t === 3) return dr === 2 * f && (dc === 1 || dc === -1);
  if (dr >= -1 && dr <= 1 && dc >= -1 && dc <= 1) return STEP_HIT[t * 9 + (dr * f + 1) * 3 + (dc + 1)] === 1;
  let sr;
  let sc;
  if (dr === 0 || dc === 0) {
    if (!(t === 7 || t === 15 || (t === 2 && dc === 0 && dr * f > 0))) return false;
  } else if (dr === dc || dr === -dc) {
    if (!(t === 6 || t === 14)) return false;
  } else return false;
  sr = Math.sign(dr);
  sc = Math.sign(dc);
  let rr = tr + sr;
  let cc = tc + sc;
  while (rr !== er || cc !== ec) {
    const q = rr * 9 + cc;
    if (e._sq[q] !== 0 && q !== from) return false;
    rr += sr;
    cc += sc;
  }
  return true;
}

// P/B/R için terfi mümkünken terfisiz hamle (neredeyse her zaman kötü) mi?
function isWeakNonPromotion(e, m) {
  if ((m >> 14) & 1) return false;
  const from = m & 127;
  if (from >= 81) return false;
  const p = e._sq[from];
  const t = p > 0 ? p : -p;
  if (t !== 1 && t !== 6 && t !== 7) return false;
  const side = p > 0 ? 1 : -1;
  const fr = (from / 9) | 0;
  const tr = ((m >> 7) & 127) / 9 | 0;
  return side > 0 ? fr <= 2 || tr <= 2 : fr >= 6 || tr >= 6;
}

function isUchifuzumeAfterMake(e, m, side) {
  if ((m & 127) !== 82) return false;
  const to = (m >> 7) & 127;
  const ek = e._king[side > 0 ? 1 : 0];
  if (ek < 0 || ek !== to + (side > 0 ? -9 : 9)) return false;
  return !e._hasLegal(-side);
}

// ------------------------------------------------------------------ Arama

class Searcher {
  constructor(engine, cfg) {
    this.e = engine;
    this.cfg = cfg;
    this.tt = new Map();
    this.killers = new Int32Array(256 * 2);
    this.hist = new Int32Array(2 * 32768);
    this.nodes = 0;
    this.stop = false;
    this.deadline = Infinity;
    this.rootDepth = 1;
  }

  checkTime() {
    if ((++this.nodes & 1023) === 0 && now() > this.deadline) this.stop = true;
    return this.stop;
  }

  orderMoves(list, side, ttMove, ply) {
    const e = this.e;
    const B = e._sq;
    const n = list.length;
    const keys = new Float64Array(n);
    const k1 = this.killers[ply * 2];
    const k2 = this.killers[ply * 2 + 1];
    const hBase = side > 0 ? 0 : 32768;
    for (let i = 0; i < n; i++) {
      const m = list[i];
      let s;
      if (m === ttMove) s = 9000000;
      else {
        const from = m & 127;
        const to = (m >> 7) & 127;
        const cap = B[to];
        if (cap !== 0) {
          const at = from >= 81 ? 0 : B[from] > 0 ? B[from] : -B[from];
          s = 5000000 + VAL[cap > 0 ? cap : -cap] * 16 - VAL[at];
        } else if (m === k1 || m === k2) s = 4000000;
        else s = 1000000 + Math.min(this.hist[hBase + (m & 32767)], 900000);
        if ((m >> 14) & 1) {
          const at = B[from] > 0 ? B[from] : -B[from];
          s += 200000 + (VAL[at + 8] - VAL[at]) * 50;
        }
        if (s < 5000000 && givesDirectCheck(e, m, side)) s += 1500000;
        if (from >= 81 && s < 2500000) s -= 300000;
      }
      keys[i] = s * 32768 + (m & 32767);
    }
    keys.sort();
    const out = new Int32Array(n);
    for (let i = 0; i < n; i++) out[i] = keys[n - 1 - i] % 32768;
    return out;
  }

  qsearch(alpha, beta, ply, qd) {
    const e = this.e;
    if (this.checkTime()) return 0;
    const stand = evaluate(e, this.cfg.expertEval);
    if (stand >= beta) return stand;
    if (stand > alpha) alpha = stand;
    if (qd >= this.cfg.qMax || ply >= 120) return stand;
    const side = e._stm;
    const list = e._gen(side, [], true);
    if (!list.length) return stand;
    const ordered = this.orderMoves(list, side, 0, Math.min(ply, 255));
    const B = e._sq;
    let best = stand;
    for (let i = 0; i < ordered.length; i++) {
      const m = ordered[i];
      if (isWeakNonPromotion(e, m)) continue;
      const cap = B[(m >> 7) & 127];
      const ct = cap > 0 ? cap : -cap;
      if (ct !== 8 && stand + VAL[ct] + 250 < alpha && !((m >> 14) & 1)) continue; // delta budama
      e._make(m, side);
      if (e._inCheck(side)) {
        e._unmake();
        continue;
      }
      const score = ct === 8 ? MATE - ply : -this.qsearch(-beta, -alpha, ply + 1, qd + 1);
      e._unmake();
      if (this.stop) return 0;
      if (score > best) {
        best = score;
        if (score > alpha) {
          alpha = score;
          if (alpha >= beta) break;
        }
      }
    }
    return best;
  }

  search(depth, alpha, beta, ply, inCheck) {
    const e = this.e;
    if (depth <= 0 && !inCheck) return this.qsearch(alpha, beta, ply, 0);
    if (this.checkTime()) return 0;
    if (ply >= 120) return evaluate(e, this.cfg.expertEval);
    if (depth < 0) depth = 0;
    // mat mesafesi budaması
    const mAlpha = -MATE + ply;
    const mBeta = MATE - ply - 1;
    if (alpha < mAlpha) alpha = mAlpha;
    if (beta > mBeta) beta = mBeta;
    if (alpha >= beta) return alpha;

    const side = e._stm;
    const key = e.hashKey();
    const tte = this.tt.get(key);
    let ttMove = 0;
    if (tte) {
      ttMove = tte.move;
      if (tte.depth >= depth) {
        let s = tte.score;
        if (s > MATE_BOUND) s -= ply;
        else if (s < -MATE_BOUND) s += ply;
        if (tte.flag === 0) return s;
        if (tte.flag === 1 && s >= beta) return s;
        if (tte.flag === 2 && s <= alpha) return s;
      }
    }

    let staticEval = 0;
    const canFutility = !inCheck && depth <= 2;
    if (canFutility) staticEval = evaluate(e, this.cfg.expertEval);

    const list = e._gen(side, [], false);
    const ordered = this.orderMoves(list, side, ttMove, ply);
    const B = e._sq;
    const origAlpha = alpha;
    let best = -INF;
    let bestMove = 0;
    let legal = 0;
    for (let i = 0; i < ordered.length; i++) {
      const m = ordered[i];
      if (isWeakNonPromotion(e, m)) continue;
      const to = (m >> 7) & 127;
      const isCap = B[to] !== 0;
      const isPromo = (m >> 14) & 1;
      e._make(m, side);
      if (e._inCheck(side) || isUchifuzumeAfterMake(e, m, side)) {
        e._unmake();
        continue;
      }
      legal++;
      const givesCheck = e._inCheck(-side);
      const quiet = !isCap && !isPromo && !givesCheck;
      if (canFutility && quiet && legal > 1 && staticEval + (depth === 1 ? 180 : 420) <= alpha) {
        e._unmake();
        continue;
      }
      const ext = givesCheck && ply < this.rootDepth * 2 && depth <= 3 ? 1 : 0;
      const nd = depth - 1 + ext;
      let score;
      if (legal === 1) {
        score = -this.search(nd, -beta, -alpha, ply + 1, givesCheck);
      } else {
        let r = 0;
        if (depth >= 3 && quiet && !inCheck && legal > 3) r = legal > 12 ? 2 : 1;
        score = -this.search(nd - r, -alpha - 1, -alpha, ply + 1, givesCheck);
        if (!this.stop && score > alpha && (r > 0 || score < beta)) {
          score = -this.search(nd, -beta, -alpha, ply + 1, givesCheck);
        }
      }
      e._unmake();
      if (this.stop) return 0;
      if (score > best) {
        best = score;
        bestMove = m;
        if (score > alpha) {
          alpha = score;
          if (alpha >= beta) {
            if (quiet) {
              if (this.killers[ply * 2] !== m) {
                this.killers[ply * 2 + 1] = this.killers[ply * 2];
                this.killers[ply * 2] = m;
              }
              this.hist[(side > 0 ? 0 : 32768) + (m & 32767)] += depth * depth;
            }
            break;
          }
        }
      }
    }
    if (legal === 0) return -MATE + ply; // şogide pat yok: hamlesiz taraf kaybeder

    let stored = best;
    if (stored > MATE_BOUND) stored += ply;
    else if (stored < -MATE_BOUND) stored -= ply;
    const flag = best >= beta ? 1 : best <= origAlpha ? 2 : 0;
    if (this.tt.size > 400000) this.tt.clear();
    this.tt.set(key, { depth, score: stored, flag, move: bestMove });
    return best;
  }

  // Kök: iteratif derinleştirme + kök gürültüsü (pencereyle doğru biçimde)
  run() {
    const e = this.e;
    const cfg = this.cfg;
    const side = e._stm;
    const start = now();
    this.deadline = start + cfg.timeMs;
    let rootMoves = Array.from(e._legalList(side));
    if (!rootMoves.length) return { move: 0, score: -MATE, depth: 0, nodes: 0, ms: 0 };
    const strong = rootMoves.filter((m) => !isWeakNonPromotion(e, m));
    if (strong.length) rootMoves = strong;
    if (rootMoves.length === 1) return { move: rootMoves[0], score: 0, depth: 0, nodes: 0, ms: now() - start };

    const noise = new Map();
    for (const m of rootMoves) noise.set(m, cfg.noise ? Math.random() * cfg.noise : 0);
    const prevScore = new Map();
    let bestMove = rootMoves[0];
    let bestScore = -INF;
    let depthDone = 0;
    for (let depth = 1; depth <= cfg.maxDepth; depth++) {
      this.rootDepth = depth;
      // önceki iterasyonun skoruna göre sırala (ilk iterasyonda ordering sezgisi)
      if (depth === 1) rootMoves = Array.from(this.orderMoves(rootMoves, side, 0, 0));
      else rootMoves.sort((a, b) => (prevScore.get(b) ?? -INF) - (prevScore.get(a) ?? -INF));
      let iterBest = 0;
      let iterBestTotal = -INF;
      let iterBestScore = -INF;
      let completed = 0;
      for (const m of rootMoves) {
        const nz = noise.get(m);
        e._make(m, side);
        const givesCheck = e._inCheck(-side);
        let score;
        if (depth === 1 && cfg.qMax === 0) {
          // easy: tek ply statik değerlendirme (taş geri alımını görmez)
          score = e._hasLegal(-side) ? -evaluate(e, false) : MATE - 1;
        } else {
          const alphaR = iterBestTotal === -INF ? -INF : iterBestTotal - nz;
          const nd = depth - 1 + (givesCheck && depth <= 3 ? 1 : 0);
          score = -this.search(nd, -INF, -alphaR, 1, givesCheck);
        }
        e._unmake();
        if (this.stop) break;
        completed++;
        prevScore.set(m, score);
        const total = score + nz;
        if (total > iterBestTotal) {
          iterBestTotal = total;
          iterBest = m;
          iterBestScore = score;
        }
      }
      // kısmi iterasyon da geçerli: ilk aranan hamle önceki en iyidir
      if (completed > 0) {
        bestMove = iterBest;
        bestScore = iterBestScore;
      }
      if (!this.stop) depthDone = depth;
      if (this.stop) break;
      if (bestScore >= MATE_BOUND) break; // mat bulundu, hemen oyna
      if (now() - start > cfg.timeMs * 0.45) break; // bir sonraki iterasyon yetişmez
    }
    return { move: bestMove, score: bestScore, depth: depthDone, nodes: this.nodes, ms: now() - start };
  }
}

// Ayrıntılı sonuç (test/teşhis için): { move: tanımlayıcı|null, score, depth, nodes, ms }
export function analyze(engine, { level = 'medium', timeMs } = {}) {
  const cfg = { ...(LEVEL_CONFIG[level] || LEVEL_CONFIG.medium) };
  if (timeMs) cfg.timeMs = timeMs;
  // aramayı motorun bir kopyasında yap: çağıranın motoru asla bozulmasın
  const e = engine.clone();
  const res = new Searcher(e, cfg).run();
  return { ...res, move: res.move ? e.descriptorFromCode(res.move) : null };
}

// Tanımlayıcı döner: { kind:'move', from, to, promote } | { kind:'drop', pieceType, to } | null
export function chooseMove(engine, { level = 'medium' } = {}) {
  return analyze(engine, { level }).move;
}

// Seçilen hamleyi doğrudan motor üzerinde oynatır. Oynanan hamle kaydını döner (ya da hamle yoksa null).
export function playAiMove(engine, options) {
  const choice = chooseMove(engine, options);
  if (!choice) return null; // mat: yapay zekanın oynayacak hamlesi yok
  if (choice.kind === 'move') {
    return engine.move({ from: choice.from, to: choice.to, promote: choice.promote });
  }
  return engine.drop({ type: choice.pieceType, row: choice.to.row, col: choice.to.col });
}

// ------------------------------------------------------------------ Web Worker

let worker = null;
let workerBroken = false;
let reqId = 0;
const pending = new Map();

function getWorker() {
  if (workerBroken || typeof Worker === 'undefined') return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL('./aiWorker.js', import.meta.url));
    worker.onmessage = (ev) => {
      const { id, move, error } = ev.data || {};
      const p = pending.get(id);
      if (!p) return;
      pending.delete(id);
      clearTimeout(p.timer);
      if (error) p.fallback();
      else p.resolve(move || null);
    };
    worker.onerror = () => {
      workerBroken = true;
      const all = Array.from(pending.values());
      pending.clear();
      all.forEach((p) => {
        clearTimeout(p.timer);
        p.fallback();
      });
      try {
        worker.terminate();
      } catch (err) {
        // yoksay
      }
      worker = null;
    };
  } catch (err) {
    workerBroken = true;
    worker = null;
  }
  return worker;
}

// Hamleyi mümkünse Web Worker'da hesaplar; olmazsa senkron chooseMove'a düşer.
export function computeAiMove(engine, { level = 'medium' } = {}) {
  const sfen = engine.toSfen();
  const syncFallback = () => chooseMove(Shogi.fromSfen(sfen), { level });
  return new Promise((resolve) => {
    const w = getWorker();
    if (!w) {
      setTimeout(() => resolve(syncFallback()), 0);
      return;
    }
    const id = ++reqId;
    const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG.medium;
    const entry = {
      resolve,
      fallback: () => resolve(syncFallback()),
      timer: setTimeout(() => {
        if (!pending.has(id)) return;
        pending.delete(id);
        entry.fallback();
      }, cfg.timeMs + 5000),
    };
    pending.set(id, entry);
    try {
      w.postMessage({ id, sfen, level });
    } catch (err) {
      pending.delete(id);
      clearTimeout(entry.timer);
      entry.fallback();
    }
  });
}

